import { useState } from "react";
import { useNavigate } from "react-router-dom";
import Loading from "../components/Loading";
import { useGetMatkaCompletedEventsQuery } from "../services/matka/matkaApi";

const PAGE_SIZE = 10;

const toApiDate = (val) => {
  if (!val) return '';
  const [y, m, d] = val.split('-');
  return `${d}-${m}-${y}`;
};

const MARKETS = ['desawar', 'faridabad', 'ghaziabad', 'gali'];

const CompletedMatkaPage = () => {
  const navigate = useNavigate();
  const [page, setPage]         = useState(1);
  const [market, setMarket]     = useState('');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate]     = useState('');
  const [applied, setApplied]   = useState({ market: '', fromDate: '', toDate: '' });

  const { data, isLoading, error, refetch } = useGetMatkaCompletedEventsQuery({
    page,
    limit: PAGE_SIZE,
    market:   applied.market,
    fromDate: applied.fromDate,
    toDate:   applied.toDate,
  });

  const games      = data?.data          || [];
  const pagination = data?.pagination    || {};
  const totalPages = pagination.totalPages || 1;
  const totalPL    = data?.summary?.totalPL ?? 0;

  const getEventId = (game) =>
    game.filterDate
      ? `${game.filterDate.split('-').reverse().join('-')}-${game.market}`
      : game.eventName?.toLowerCase().replace(' ', '-') || '';

  const handleSearch = () => {
    setPage(1);
    setApplied({
      market,
      fromDate: toApiDate(fromDate),
      toDate:   toApiDate(toDate),
    });
  };

  const handleReset = () => {
    setMarket(''); setFromDate(''); setToDate('');
    setPage(1);
    setApplied({ market: '', fromDate: '', toDate: '' });
  };

  return (
    <div className="w-full">
      <div className="bg-[#0F2327] px-5 py-3 flex items-center justify-between">
        <span className="text-white text-xl font-semibold">Completed Matka</span>
        <div className="flex gap-2">
          <button onClick={refetch} className="bg-green-600 hover:bg-green-700 text-white px-4 py-1.5 text-sm transition-colors">
            Refresh
          </button>
          <button onClick={() => navigate(-1)} className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-1.5 text-sm transition-colors">
            Back
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-end gap-3 px-4 py-3 bg-gray-50 border-b border-gray-200">
        <div className="flex flex-col gap-1">
          <label className="text-xs text-gray-500 font-medium">Market</label>
          <select
            value={market}
            onChange={(e) => setMarket(e.target.value)}
            className="border border-gray-300 px-3 py-1.5 text-sm rounded focus:outline-none focus:border-blue-400 bg-white min-w-[130px]"
          >
            <option value="">All Markets</option>
            {MARKETS.map(m => <option key={m} value={m}>{m.charAt(0).toUpperCase() + m.slice(1)}</option>)}
          </select>
        </div>
        <div className="flex flex-col gap-1">
          <label className="text-xs text-gray-500 font-medium">From Date</label>
          <input
            type="date"
            value={fromDate}
            onChange={(e) => setFromDate(e.target.value)}
            className="border border-gray-300 px-3 py-1.5 text-sm rounded focus:outline-none focus:border-blue-400"
          />
        </div>
        <div className="flex flex-col gap-1">
          <label className="text-xs text-gray-500 font-medium">To Date</label>
          <input
            type="date"
            value={toDate}
            onChange={(e) => setToDate(e.target.value)}
            className="border border-gray-300 px-3 py-1.5 text-sm rounded focus:outline-none focus:border-blue-400"
          />
        </div>
        <button
          onClick={handleSearch}
          className="bg-[#0F2327] hover:bg-[#1a3a40] text-white px-5 py-1.5 text-sm rounded transition-colors"
        >
          Search
        </button>
        <button
          onClick={handleReset}
          className="bg-gray-200 hover:bg-gray-300 text-gray-700 px-4 py-1.5 text-sm rounded transition-colors"
        >
          Reset
        </button>
      </div>

      {error && (
        <div className="mx-4 my-3 p-3 bg-red-100 text-red-700 border border-red-300 text-sm">
          {error?.data?.message || "Failed to load completed matka games"}
        </div>
      )}

      {/* P/L Summary Card */}
      <div className="p-4">
        <div className="bg-gray-100 rounded-lg px-6 py-4 inline-block min-w-[160px]">
          <div className="text-gray-500 text-sm mb-1">P/L</div>
          <div className={`text-2xl font-semibold ${totalPL >= 0 ? 'text-green-600' : 'text-red-500'}`}>
            {totalPL.toFixed(2)}
          </div>
        </div>
      </div>

      <div className="overflow-x-auto">
        {isLoading ? (
          <div className="flex justify-center py-16"><Loading /></div>
        ) : (
          <>
            <table className="w-full border-collapse text-sm">
              <thead>
                <tr className="bg-[#0F2327] text-white">
                  <th className="px-4 py-3 text-left font-semibold border border-[#1a3a40]">Date</th>
                  <th className="px-4 py-3 text-left font-semibold border border-[#1a3a40]">Event Name</th>
                  <th className="px-4 py-3 text-left font-semibold border border-[#1a3a40]">Result</th>
                  <th className="px-4 py-3 text-left font-semibold border border-[#1a3a40]">P/L</th>
                  <th className="px-4 py-3 text-left font-semibold border border-[#1a3a40]">Actions</th>
                </tr>
              </thead>
              <tbody>
                {games.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="text-center py-12 text-gray-400 border border-gray-200">
                      No completed matka games found
                    </td>
                  </tr>
                ) : (
                  games.map((game, idx) => {
                    const pl      = game.netAmount ?? game.pl ?? 0;
                    const eventId = game.eventId || getEventId(game);
                    return (
                      <tr key={eventId || idx} className={idx % 2 === 0 ? "bg-white" : "bg-gray-50"}>
                        <td className="px-4 py-2.5 border border-gray-200">{game.date || '-'}</td>
                        <td className="px-4 py-2.5 border border-gray-200 capitalize font-medium text-gray-800">{game.eventName || '-'}</td>
                        <td className="px-4 py-2.5 border border-gray-200 font-semibold">{game.result || '-'}</td>
                        <td className={`px-4 py-2.5 border border-gray-200 font-medium ${pl >= 0 ? 'text-green-600' : 'text-red-500'}`}>
                          {Number(pl).toFixed(2)}
                        </td>
                        <td className="px-4 py-2.5 border border-gray-200">
                          <div className="flex items-center gap-2">
                            <button
                              onClick={() => navigate(`/matka/all-bets/${eventId}/${encodeURIComponent(game.eventName || '')}`)}
                              className="bg-[#0F2327] hover:bg-[#1a3a40] text-white px-3 py-1.5 text-xs font-medium transition-colors rounded-sm"
                            >
                              Show Bet
                            </button>
                            <button
                              onClick={() => navigate(`/matka/agent-report/${eventId}`)}
                              className="bg-[#0F2327] hover:bg-[#1a3a40] text-white px-3 py-1.5 text-xs font-medium transition-colors rounded-sm"
                            >
                              PL
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>

            {totalPages > 1 && (
              <div className="flex justify-end items-center gap-1 px-4 py-3 border-t border-gray-200 bg-white">
                <button
                  onClick={() => setPage(p => Math.max(1, p - 1))}
                  disabled={page === 1}
                  className="w-8 h-8 flex items-center justify-center border border-gray-300 text-gray-500 disabled:opacity-40 hover:bg-gray-50 text-sm"
                >
                  ‹
                </button>
                {Array.from({ length: totalPages }, (_, i) => i + 1).map(p => (
                  <button
                    key={p}
                    onClick={() => setPage(p)}
                    className={`w-8 h-8 flex items-center justify-center border text-sm font-medium ${
                      page === p ? 'border-blue-500 bg-blue-500 text-white' : 'border-gray-300 text-gray-600 hover:bg-gray-50'
                    }`}
                  >
                    {p}
                  </button>
                ))}
                <button
                  onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                  disabled={page >= totalPages}
                  className="w-8 h-8 flex items-center justify-center border border-gray-300 text-gray-500 disabled:opacity-40 hover:bg-gray-50 text-sm"
                >
                  ›
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default CompletedMatkaPage;
